import { useAppDispatch } from 'app/store';
import { useNavigate } from 'react-router-dom';
import {
  attachAudioToReleaseEffect,
  createReleaseEffect,
} from 'app/store/Releases/effects';
import { Release, ReleaseType } from 'types/releases';
import { routes } from 'app/routing/routesConfig';
import queryString from 'query-string';
import { updateReleaseStatus } from 'app/store/Releases/slice';
import { SelectedAudio, SelectedAudioObject } from 'app/components/Audio';

interface UseCreateReleaseProps {
  sendSnackbarMessage: (id: string) => void;
  releaseType?: ReleaseType;
}

export const useCreateRelease = ({
  sendSnackbarMessage,
  releaseType,
}: UseCreateReleaseProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleCreateRelease = async (selectedAudio: SelectedAudioObject) => {
    const selectedAudioArray: SelectedAudio[] = Object.values(selectedAudio);
    const release: Release = await dispatch(
      createReleaseEffect({ type: releaseType })
    ).unwrap();

    await dispatch(
      attachAudioToReleaseEffect({
        releaseGuid: release.guid,
        selectedAudioArray,
        link: release._links?.createReleaseVersion,
      })
    ).unwrap();

    sendSnackbarMessage(release.guid);
    navigate({
      pathname: routes.release.path.replace(':guid', release.guid),
      search: queryString.stringify({ shouldNotFetch: true }),
    });
    return release;
  };

  return { handleCreateRelease };
};

interface UseAddVersionsProps {
  release?: Release;
  sendSnackbarMessage: (id: string) => void;
  onCancel?: () => void;
}

export const useAddVersions = ({
  release,
  sendSnackbarMessage,
  onCancel,
}: UseAddVersionsProps) => {
  const dispatch = useAppDispatch();

  const handleAttachMoreVersions = async (
    selectedAudio: SelectedAudioObject
  ) => {
    if (!release) {
      return;
    }
    dispatch(updateReleaseStatus('Processing'));
    const selectedAudioArray: SelectedAudio[] = Object.values(selectedAudio);
    try {
      await dispatch(
        attachAudioToReleaseEffect({
          releaseGuid: release.guid,
          selectedAudioArray,
          link: release._links?.createReleaseVersion,
        })
      ).unwrap();
      sendSnackbarMessage(release.guid);

      if (onCancel) {
        onCancel();
      }
    } catch (e) {
      dispatch(updateReleaseStatus('Ready'));
    }
  };

  return { handleAttachMoreVersions };
};
